/**
 * Bulk import: subes múltiples archivos a la vez (PDFs de BBVA/Inbursa,
 * CSV de Konfio). El cliente parsea cada uno con el parser correspondiente,
 * muestra preview consolidado, y manda todo en un único POST.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Upload,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Building2,
  CreditCard,
  FileSpreadsheet,
  Trash2,
} from "lucide-react";
import clsx from "clsx";
import type { Account } from "@axorial/shared";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";
import type { BankKind, BulkParseResult, BulkParsedRow } from "./types";
import { extractPdfText } from "./pdf-text";
import { parseBBVA } from "./bbva";
import { parseInbursa } from "./inbursa";
import { parseKonfioCsv } from "./konfio-csv";
import { parseKonfioPdf } from "./konfio-pdf";

interface ParsedFile {
  id: string;
  filename: string;
  bank: BankKind;
  accountId: string; // a qué cuenta del panel asignar
  result: BulkParseResult;
  status: "ok" | "warning" | "error";
  // Periodo editable. Inicializado con lo que detectó el parser.
  periodStart: string;
  periodEnd: string;
}

const BANK_LABEL: Record<BankKind, string> = {
  bbva: "BBVA",
  inbursa: "Inbursa",
  konfio: "Konfio",
  csv_generic: "CSV genérico",
};

interface BankDropConfig {
  bank: BankKind;
  label: string;
  description: string;
  accept: string;
  icon: typeof Building2;
  color: string;
}

const BANK_DROPZONES: BankDropConfig[] = [
  {
    bank: "bbva",
    label: "BBVA",
    description: "Estados de cuenta empresariales (PDF)",
    accept: ".pdf",
    icon: Building2,
    color: "text-info",
  },
  {
    bank: "inbursa",
    label: "Inbursa",
    description: "Estados de cuenta empresariales (PDF)",
    accept: ".pdf",
    icon: Building2,
    color: "text-success",
  },
  {
    bank: "konfio",
    label: "Konfio",
    description: "CSV de movimientos o PDFs mensuales de la TDC",
    accept: ".csv,.pdf",
    icon: CreditCard,
    color: "text-warning",
  },
  {
    bank: "csv_generic",
    label: "Otro / CSV",
    description: "Cualquier CSV o XLSX (mapeo manual abajo)",
    accept: ".csv,.xlsx",
    icon: FileSpreadsheet,
    color: "text-base-content/55",
  },
];

export function BulkImportPanel() {
  const qc = useQueryClient();
  const [files, setFiles] = useState<ParsedFile[]>([]);
  const [parsing, setParsing] = useState(false);
  const [defaults, setDefaults] = useState({
    affectsTotals: true,
    isrApplicable: true,
    invoiceStatus: "pendiente" as "facturado" | "deducible_sin_factura" | "pendiente" | "no_aplica",
  });

  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });

  async function handleFiles(fileList: FileList | File[], forcedBank: BankKind) {
    setParsing(true);
    const newParsed: ParsedFile[] = [];
    for (const f of Array.from(fileList)) {
      const id = crypto.randomUUID();
      try {
        let raw: string;
        if (f.name.toLowerCase().endsWith(".csv")) {
          raw = await f.text();
        } else {
          raw = await extractPdfText(f);
        }
        const isPdf = f.name.toLowerCase().endsWith(".pdf");
        let result: BulkParseResult;
        if (forcedBank === "bbva") result = parseBBVA(raw);
        else if (forcedBank === "inbursa") result = parseInbursa(raw);
        else if (forcedBank === "konfio")
          result = isPdf ? parseKonfioPdf(raw) : parseKonfioCsv(raw);
        else
          result = {
            bank: "csv_generic",
            rows: [],
            errors: [
              "Para CSV custom usa la pestaña 'CSV / XLSX custom' arriba — soporta mapeo manual de columnas.",
            ],
          };

        const status: ParsedFile["status"] =
          result.rows.length === 0 ? "error" : result.errors.length > 0 ? "warning" : "ok";
        newParsed.push({
          id,
          filename: f.name,
          bank: forcedBank,
          accountId: suggestAccount(forcedBank, accounts.data?.accounts ?? []),
          result,
          status,
          periodStart: result.periodStart ?? "",
          periodEnd: result.periodEnd ?? "",
        });
      } catch (e) {
        newParsed.push({
          id,
          filename: f.name,
          bank: forcedBank,
          accountId: "",
          result: { bank: forcedBank, rows: [], errors: [String(e)] },
          status: "error",
          periodStart: "",
          periodEnd: "",
        });
      }
    }
    setFiles((prev) => [...prev, ...newParsed]);
    setParsing(false);
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function setAccount(id: string, accountId: string) {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, accountId } : f)));
  }

  function setPeriod(id: string, periodStart: string, periodEnd: string) {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, periodStart, periodEnd } : f)));
  }

  const submit = useMutation<{
    ok: true;
    batchId: string;
    inserted: number;
    transfersCreated: number;
    mirrorsMarked: number;
    skippedDuplicates: number;
  }>({
    mutationFn: () => {
      const valid = files.filter((f) => f.status !== "error" && f.accountId);
      return api.post("/finance/import/bulk", {
        defaults,
        imports: valid.map((f) => ({
          accountId: f.accountId,
          bankLabel: f.bank,
          rows: f.result.rows.map((r) => stripExtraFields(r)),
        })),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance"] });
    },
  });

  const totalRows = files.reduce((sum, f) => sum + f.result.rows.length, 0);
  const totalErrors = files.reduce((sum, f) => sum + f.result.errors.length, 0);
  const hasUnassigned = files.some((f) => f.status !== "error" && !f.accountId);
  const validFiles = files.filter((f) => f.status !== "error");

  return (
    <div className="space-y-4">
      <ResetPanel />

      <div className="card-elevated p-5 space-y-4">
        <div>
          <h3 className="text-base font-display font-semibold mb-1">Subir por tipo</h3>
          <p className="text-sm text-base-content/65">
            Cada zona usa un parser específico. El sistema detecta transferencias entre cuentas
            propias automáticamente al importar.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {BANK_DROPZONES.map((cfg) => {
            const filesOfBank = files.filter((f) => f.bank === cfg.bank);
            return (
              <BankDropZone
                key={cfg.bank}
                cfg={cfg}
                count={filesOfBank.length}
                disabled={parsing}
                onFiles={(fl) => void handleFiles(fl, cfg.bank)}
              />
            );
          })}
        </div>

        {parsing && (
          <div className="flex items-center gap-2 text-sm text-base-content/65">
            <Loader2 className="size-4 animate-spin text-primary" /> Procesando archivos…
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="card-elevated p-5 space-y-3">
          <h4 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
            Archivos detectados ({files.length})
          </h4>
          <ul className="space-y-2">
            {files.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                accounts={accounts.data?.accounts ?? []}
                onSetAccount={(id) => setAccount(f.id, id)}
                onSetPeriod={(start, end) => setPeriod(f.id, start, end)}
                onRemove={() => removeFile(f.id)}
              />
            ))}
          </ul>
        </div>
      )}

      {validFiles.length > 0 && (
        <div className="card-elevated p-5 space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
            Defaults para todas las filas
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ToggleField
              label="Afecta saldos"
              checked={defaults.affectsTotals}
              onChange={(v) => setDefaults((d) => ({ ...d, affectsTotals: v }))}
              help="Si es histórico ya en saldo inicial, desactiva."
            />
            <ToggleField
              label="Aplica para ISR/IVA"
              checked={defaults.isrApplicable}
              onChange={(v) => setDefaults((d) => ({ ...d, isrApplicable: v }))}
              help="Default ON. Las transferencias internas se desactivan automáticamente."
            />
            <label className="form-control block">
              <div className="label py-1">
                <span className="label-text text-sm font-medium">Status factura default</span>
              </div>
              <select
                value={defaults.invoiceStatus}
                onChange={(e) =>
                  setDefaults((d) => ({
                    ...d,
                    invoiceStatus: e.target.value as typeof defaults.invoiceStatus,
                  }))
                }
                className="select select-bordered select-sm w-full"
              >
                <option value="pendiente">Pendiente</option>
                <option value="facturado">Con CFDI</option>
                <option value="deducible_sin_factura">Deducible sin CFDI</option>
                <option value="no_aplica">No deducible</option>
              </select>
            </label>
          </div>

          <div className="flex items-center justify-between border-t border-base-300/60 pt-3">
            <div className="text-xs text-base-content/65">
              {totalRows} filas totales
              {totalErrors > 0 && (
                <span className="text-warning ml-2">· {totalErrors} líneas con error</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => submit.mutate()}
              disabled={submit.isPending || hasUnassigned || validFiles.length === 0}
              className="btn btn-primary btn-sm"
            >
              {submit.isPending && <span className="loading loading-spinner loading-xs" />}
              Importar todo
            </button>
          </div>
          {hasUnassigned && (
            <div className="alert alert-warning text-xs py-2">
              Asigna una cuenta del panel a cada archivo antes de importar.
            </div>
          )}
        </div>
      )}

      {submit.isSuccess && submit.data && (
        <div className="alert alert-success text-sm">
          <CheckCircle2 className="size-4" />
          <div>
            Importación completa: <strong>{submit.data.inserted}</strong> transacciones,{" "}
            {submit.data.transfersCreated} transferencias internas detectadas,{" "}
            {submit.data.mirrorsMarked} espejos marcados y{" "}
            <strong>{submit.data.skippedDuplicates}</strong> duplicados saltados.
            <div className="text-xs opacity-75 mt-1">Batch: {submit.data.batchId}</div>
          </div>
        </div>
      )}

      {submit.isError && (
        <div className="alert alert-error text-sm">
          {submit.error instanceof Error ? submit.error.message : "Error al importar"}
        </div>
      )}
    </div>
  );
}

interface ResetCounts {
  transactions: number;
  transfers: number;
  snapshots: number;
  documents: number;
  accounts: number;
  accountsWithOpening: number;
}

function ResetPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const counts = useQuery<ResetCounts>({
    queryKey: ["finance", "reset", "counts"],
    queryFn: () => api.get("/finance/reset/counts"),
    enabled: open,
  });

  return (
    <div className="card-elevated p-4 border border-warning/30 bg-warning/5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <AlertTriangle className="size-4 text-warning shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">Empezar de cero</div>
            <p className="text-xs text-base-content/65 mt-0.5">
              Borra todos los movimientos antes de subir el bulk. Las cuentas y categorías se
              quedan.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn btn-sm btn-outline btn-warning"
        >
          <Trash2 className="size-3.5" /> Limpiar movimientos
        </button>
      </div>

      {open && counts.data && (
        <ResetDialog
          counts={counts.data}
          onClose={() => setOpen(false)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ["finance"] });
            setOpen(false);
          }}
        />
      )}
      {open && counts.isLoading && (
        <div className="modal modal-open">
          <div className="modal-box max-w-sm grid place-items-center py-12">
            <span className="loading loading-spinner text-primary" />
          </div>
        </div>
      )}
    </div>
  );
}

function ResetDialog({
  counts,
  onClose,
  onDone,
}: {
  counts: ResetCounts;
  onClose: () => void;
  onDone: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [alsoResetOpeningBalances, setAlsoResetOpeningBalances] = useState(false);
  const [alsoDeleteSnapshots, setAlsoDeleteSnapshots] = useState(true);
  const [alsoDeleteDocuments, setAlsoDeleteDocuments] = useState(false);

  const reset = useMutation<{
    ok: true;
    transactions: number;
    transfers: number;
    snapshots: number;
    documents: number;
    openingBalancesReset: number;
  }>({
    mutationFn: () =>
      api.post("/finance/reset", {
        confirm: "RESET_FINANCE_DATA",
        alsoResetOpeningBalances,
        alsoDeleteSnapshots,
        alsoDeleteDocuments,
      }),
    onSuccess: () => onDone(),
  });

  const ready = confirmText === "ELIMINAR" && !reset.isPending;

  return (
    <div className="modal modal-open" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box max-w-lg">
        <h3 className="font-display font-semibold text-lg text-error mb-3">
          Limpiar movimientos financieros
        </h3>

        <div className="alert alert-warning text-sm py-2 mb-3">
          <AlertTriangle className="size-4 shrink-0" />
          <div>Esta acción es irreversible. No hay deshacer.</div>
        </div>

        <div className="space-y-2 text-sm">
          <p className="text-base-content/65">Se borrarán:</p>
          <ul className="list-disc ml-5 text-sm space-y-0.5">
            <li>
              <strong>{counts.transactions}</strong> transacciones (ingresos / gastos)
            </li>
            <li>
              <strong>{counts.transfers}</strong> transferencias
            </li>
          </ul>

          <p className="text-base-content/65 mt-3">Opciones extra:</p>
          <div className="space-y-1.5">
            <Toggle
              label={`Resetear saldo inicial de las ${counts.accountsWithOpening} cuentas con opening ≠ 0`}
              checked={alsoResetOpeningBalances}
              onChange={setAlsoResetOpeningBalances}
            />
            <Toggle
              label={`También borrar ${counts.snapshots} snapshots de saldo`}
              checked={alsoDeleteSnapshots}
              onChange={setAlsoDeleteSnapshots}
              disabled={counts.snapshots === 0}
            />
            <Toggle
              label={`También borrar ${counts.documents} documentos del archivero (R2 incluido)`}
              checked={alsoDeleteDocuments}
              onChange={setAlsoDeleteDocuments}
              disabled={counts.documents === 0}
            />
          </div>

          <label className="form-control block mt-4">
            <div className="label py-1">
              <span className="label-text text-sm font-medium">
                Escribe <code className="font-mono text-error">ELIMINAR</code> para confirmar
              </span>
            </div>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="input input-bordered w-full font-mono"
              autoFocus
            />
          </label>
        </div>

        {reset.isError && (
          <div className="alert alert-error text-sm mt-3">
            {reset.error instanceof Error ? reset.error.message : "Error al ejecutar reset"}
          </div>
        )}

        <div className="modal-action mt-4 pt-3 border-t border-base-300/60">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => reset.mutate()}
            disabled={!ready}
            className="btn btn-error btn-sm"
          >
            {reset.isPending && <span className="loading loading-spinner loading-xs" />}
            <Trash2 className="size-3.5" />
            Eliminar todo
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={clsx("flex items-center gap-2 text-xs", disabled && "opacity-55")}>
      <input
        type="checkbox"
        className="checkbox checkbox-xs checkbox-error"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  );
}

function BankDropZone({
  cfg,
  count,
  disabled,
  onFiles,
}: {
  cfg: BankDropConfig;
  count: number;
  disabled: boolean;
  onFiles: (files: FileList) => void;
}) {
  const [drag, setDrag] = useState(false);
  const Icon = cfg.icon;
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files);
      }}
      className={clsx(
        "cursor-pointer rounded-md border-2 border-dashed p-4 transition-colors flex flex-col gap-2",
        drag
          ? "border-primary bg-primary/5"
          : "border-base-300/80 hover:border-primary/40 hover:bg-base-200/40",
        disabled && "opacity-60 pointer-events-none",
      )}
    >
      <div className="flex items-center gap-2">
        <div className={clsx("size-8 rounded-md bg-base-200 grid place-items-center", cfg.color)}>
          <Icon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{cfg.label}</div>
          {count > 0 && (
            <span className="text-xs text-success">
              {count} archivo{count === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <Upload className="size-4 text-base-content/40 shrink-0" />
      </div>
      <p className="text-xs text-base-content/55 leading-snug">{cfg.description}</p>
      <input
        type="file"
        multiple
        accept={cfg.accept}
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function FileRow({
  file,
  accounts,
  onSetAccount,
  onSetPeriod,
  onRemove,
}: {
  file: ParsedFile;
  accounts: Account[];
  onSetAccount: (id: string) => void;
  onSetPeriod: (start: string, end: string) => void;
  onRemove: () => void;
}) {
  const r = file.result;
  const total = r.rows.length;
  const ingresos = r.rows.filter((x) => x.kind === "ingreso").length;
  const gastos = r.rows.filter((x) => x.kind === "gasto").length;
  const internos = r.rows.filter((x) => x.internalTransferHint).length;
  const sumGastos = r.rows
    .filter((x) => x.kind === "gasto")
    .reduce((sum, x) => sum + x.amountCents, 0);
  const sumIngresos = r.rows
    .filter((x) => x.kind === "ingreso")
    .reduce((sum, x) => sum + x.amountCents, 0);

  return (
    <li
      className={clsx(
        "rounded-md border p-3 space-y-2",
        file.status === "error"
          ? "border-error/30 bg-error/5"
          : file.status === "warning"
            ? "border-warning/30 bg-warning/5"
            : "border-base-300/50",
      )}
    >
      <div className="flex items-start gap-3">
        <div className="size-8 rounded-md bg-base-200 grid place-items-center shrink-0 text-xs font-semibold uppercase">
          {file.bank.slice(0, 3)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{file.filename}</div>
          <div className="text-xs text-base-content/55 mt-0.5">
            {BANK_LABEL[file.bank]} ·{" "}
            {r.periodStart && r.periodEnd ? `${r.periodStart} → ${r.periodEnd}` : "sin periodo"}
            {r.accountHint && ` · cuenta ${r.accountHint}`}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="btn btn-ghost btn-xs btn-square text-base-content/55"
          aria-label="Quitar"
        >
          <X className="size-3.5" />
        </button>
      </div>

      {file.status !== "error" && (
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="text-base-content/65">
            {total} filas · {ingresos} ingresos · {gastos} gastos
          </span>
          {internos > 0 && (
            <span className="badge badge-info badge-xs">
              {internos} transfer{internos === 1 ? "" : "s"} interno{internos === 1 ? "" : "s"}
            </span>
          )}
          <span className="text-success font-mono tabular-nums">
            +{formatMxnCents(sumIngresos)}
          </span>
          <span className="text-error font-mono tabular-nums">−{formatMxnCents(sumGastos)}</span>
          {r.closingBalanceCents !== undefined && (
            <span className="text-base-content/55">
              · saldo final: {formatMxnCents(r.closingBalanceCents)}
            </span>
          )}
        </div>
      )}

      {file.status !== "error" && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <label className="block">
            <span className="text-[11px] text-base-content/55 uppercase tracking-wider">
              Cuenta
            </span>
            <select
              value={file.accountId}
              onChange={(e) => onSetAccount(e.target.value)}
              className="select select-bordered select-xs w-full mt-0.5"
            >
              <option value="">— Selecciona —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] text-base-content/55 uppercase tracking-wider">
              Periodo desde
            </span>
            <input
              type="date"
              value={file.periodStart}
              onChange={(e) => onSetPeriod(e.target.value, file.periodEnd)}
              className="input input-bordered input-xs mt-0.5"
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-base-content/55 uppercase tracking-wider">hasta</span>
            <input
              type="date"
              value={file.periodEnd}
              onChange={(e) => onSetPeriod(file.periodStart, e.target.value)}
              className="input input-bordered input-xs mt-0.5"
            />
          </label>
        </div>
      )}

      {r.errors.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-warning inline-flex items-center gap-1">
            <AlertTriangle className="size-3" />
            {r.errors.length} línea{r.errors.length === 1 ? "" : "s"} con problema
          </summary>
          <ul className="mt-1 space-y-0.5 text-base-content/55 ml-4 max-h-32 overflow-auto">
            {r.errors.slice(0, 20).map((err, i) => (
              <li key={i}>· {err}</li>
            ))}
            {r.errors.length > 20 && <li className="opacity-60">… y {r.errors.length - 20} más</li>}
          </ul>
        </details>
      )}
    </li>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
  help,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  help: string;
}) {
  return (
    <label className="cursor-pointer flex items-start gap-3 rounded-md border border-base-300/60 bg-base-200/30 p-3 min-w-0">
      <input
        type="checkbox"
        className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="text-left min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">{help}</div>
      </div>
    </label>
  );
}

function suggestAccount(bank: BankKind, accounts: Account[]): string {
  // Heurística: matchear por nombre/bankName.
  const names: Record<BankKind, RegExp[]> = {
    bbva: [/bbva/i],
    inbursa: [/inbursa/i],
    konfio: [/konfio/i],
    csv_generic: [],
  };
  const patterns = names[bank];
  for (const a of accounts) {
    const haystack = `${a.name} ${a.bankName ?? ""}`;
    if (patterns.some((p) => p.test(haystack))) return a.id;
  }
  return "";
}

/** Quita campos derivados que solo viven en el cliente. */
function stripExtraFields(r: BulkParsedRow): BulkParsedRow {
  return {
    date: r.date,
    description: r.description,
    kind: r.kind,
    amountCents: r.amountCents,
    ivaCents: r.ivaCents,
    ...(r.internalTransferHint ? { internalTransferHint: r.internalTransferHint } : {}),
    ...(r.rawCounterparty ? { rawCounterparty: r.rawCounterparty } : {}),
    ...(r.rawCode ? { rawCode: r.rawCode } : {}),
  };
}
